// Keyword-based categorization. AI-ready: this can be replaced/augmented with an LLM call later.
//
// Phase 70H — expanded to cover all 24 canonical categories. Order
// matters: more specific patterns first (e.g. 'starbucks' before 'shop'),
// because we return on first hit. The merchant_dictionary lookup (in
// the backend) is checked BEFORE this fallback, so this is the
// safety-net for merchants we haven't seeded yet.
const KEYWORDS = {
  // Daily Living
  'Food & Dining':    ['swiggy','zomato','ubereats','doordash','restaurant','cafe','starbucks','mcdonald','kfc','pizza','burger','dining','dunkin','chipotle','barista','ccd','haldiram','dominos','subway','eversub'],
  'Groceries':        ['blinkit','zepto','bigbasket','instamart','dmart','reliance fresh','grocery','supermarket','trader joe','whole foods','aldi','tesco','spencer','spar','more retail','natures basket'],
  'Fuel':             ['shell','indianoil','bharat petroleum','hp petrol','fuel','petrol','gas station','bp ','reliance bp','nayara','iocl','ev charging','tata power ez'],
  'Transport':        ['uber','ola','rapido','lyft','metro','irctc','redbus','taxi','cab','indigo','vistara','airline','train','autorickshaw','parking','fastag','toll'],
  'Car Maintenance':  ['vehicle service','car service','mechanic','car repair','auto service','tyre','tire','tyres','tires','servify car','carkraft','myTVS','carz','oil change','battery','wheel alignment','denting','car wash','carwash','vehicle repair'],
  'Utilities':        ['electricity','water bill','gas bill','broadband','airtel','jio','vi ','bsnl','comcast','verizon','utility','tata sky','dth','tatapower','adani electricity','bescom','torrent power','bsnl'],
  'Shopping':         ['amazon','flipkart','myntra','ajio','nykaa','snapdeal','ebay','shop','store','target','walmart','croma','reliance digital','vijay sales','pepperfry','urban ladder','ikea','decathlon','lifestyle','westside'],
  'Medical':          ['hospital','clinic','pharmacy','apollo','medplus','netmeds','pharmeasy','medical','doctor','diagnostic','dental','optical','lenskart','1mg','tata 1mg','fortis','max healthcare','manipal','aiims'],
  'Personal Care':    ['salon','spa','gym','fitness','cult.fit','cultfit','golds gym','barber','haircut','massage','urban company','urbanclap','vlcc','lakme'],
  // Home
  'Housing':          ['rent','landlord','lease','security deposit','broker fee','furniture','appliance','sofa','mattress','refrigerator','washing machine','air conditioner'],
  'Home Maintenance': ['plumber','electrician','carpenter','painter','painting','amc ','annual maintenance','household help','maid','cook','driver','gardener','cleaner','urban company','servify'],
  // Family
  'Education':        ['school fee','tuition','course','udemy','coursera','byju','unacademy','vedantu','great learning','college fee','university','exam fee','textbook'],
  'Childcare':        ['daycare','creche','nanny','baby','diaper','firstcry','babyhug','kids','preschool'],
  // Lifestyle
  'Entertainment':    ['bookmyshow','pvr','inox','cinepolis','movie','concert','event','game','steam','playstation','xbox','nintendo','book store','crossword'],
  'Travel':           ['makemytrip','goibibo','yatra','cleartrip','easemytrip','booking.com','airbnb','oyo','taj','marriott','hyatt','itc hotel','vacation','holiday','tourism'],
  'Subscriptions':    ['netflix','prime','spotify','hotstar','disney+','youtube premium','apple','icloud','adobe','notion','github','figma','dropbox','google one','linkedin premium','medium'],
  'Hobbies':          ['decathlon','sports','art supplies','musical','camera','photography','guitar','piano','craft','model kit','board game'],
  'Gifts':            ['gift','flowers','ferns n petals','fnp','archies','hallmark','birthday','wedding'],
  // Financial
  'Loan Payment':     ['emi','loan emi','home loan','car loan','personal loan','credit card payment','cc payment','loan repayment','hdfc emi','sbi emi','icici emi'],
  'Insurance':        ['insurance','policy bazaar','policybazaar','lic','hdfc life','icici prudential','sbi life','star health','niva bupa','acko','digit','tata aig','bajaj allianz','premium payment'],
  'Taxes':            ['income tax','advance tax','gst','tds','professional tax','property tax','tax payment','challan','income-tax'],
  'Fees & Charges':   ['bank charge','atm charge','atm fee','forex markup','late fee','convenience fee','processing fee','annual fee','joining fee','service charge','penalty'],
  // Transfers — P2P payments to individuals for work or service.
  // Conservative: only match when there is no other stronger signal
  // (autoCategorize iterates the map and the first hit wins, so we put
  // Transfers AFTER all the merchant categories above).
  'Transfers':        ['imps p2p','upi p2p','payment to','transfer to','sent to','paid to'],
  // Work
  'Business Expenses':['office','freelance','contract','client meal','business travel','aws','google cloud','digital ocean','stripe fee','razorpay fee'],
  // Giving
  'Giving':           ['donation','charity','ngo','give india','milaap','ketto','gofundme','temple','church','mosque','gurudwara','offering','tithe','seva','daana'],
};

export function autoCategorize(description, type = 'expense') {
  if (!description) {
    if (type === 'income') return 'Other Income';
    if (type === 'investment') return 'Other Investment';
    if (type === 'transfer') return 'Other Transfer';
    if (type === 'refund') return 'Refunds & Reversals';
    return 'Miscellaneous';
  }
  const d = description.toLowerCase();

  if (type === 'transfer') {
    if (isAtmWithdrawalDescription(description)) return 'Cash Withdrawal';
    if (/cash deposit|csh dep|cdm/.test(d)) return 'Cash Deposit';
    if (/reconcil/.test(d)) return 'Cash Reconciliation';
    // Phase 55 follow-up — A transfer narration that names a specific
    // person ("OF Mrs. ALEYAMMA KURIAKO" / "OF Mr. PRIYA NAIR") is almost
    // always a person-to-person transfer (gift, family support, inheritance,
    // loan from a friend), not a self-transfer between the user's own
    // accounts. Route to "Gift" so the row reads honestly and rolls into
    // the right bucket if the user later flips the type to income.
    if (/\bof\s+(mr|mrs|ms|miss|dr|mx)\.?\s+[a-z]/i.test(description)) return 'Gift';
    if (/neft|imps|rtgs|upi.*self|transfer to|transfer from/.test(d)) return 'Bank Transfer';
    return 'Other Transfer';
  }

  if (type === 'refund') {
    if (/cashback|cash\s?back|rewards? credit/i.test(description)) return 'Cashback';
    if (/failed|reversal|reverse|chargeback/i.test(description)) return 'Failed Payment Reversal';
    if (/swiggy|zomato|amazon|flipkart|myntra|uber|ola|nykaa|service/i.test(description)) return 'Service Refund';
    if (/vendor|merchant|supplier/i.test(description)) return 'Vendor Refund';
    return 'Refunds & Reversals';
  }

  if (type === 'income') {
    if (/salary|payroll/.test(d)) return 'Salary';
    if (/dividend/.test(d)) return 'Dividend';
    if (/interest/.test(d)) return 'Interest';
    if (/rent/.test(d)) return 'Rental';
    if (/bonus/.test(d)) return 'Bonus';
    if (/refund/.test(d)) return 'Refund';
    if (/freelance|contract/.test(d)) return 'Freelance';
    // Phase 55 follow-up — person-to-person inflows. Same heuristic as
    // the transfer branch: a narration that names a specific person ("OF
    // Mrs. ...", "from Mr. ...") is most often a gift or family transfer.
    if (/\b(of|from)\s+(mr|mrs|ms|miss|dr|mx)\.?\s+[a-z]/i.test(description)) return 'Gift';
    if (/\bgift\b|inheritance|family\s+transfer/i.test(description)) return 'Gift';
    return 'Other Income';
  }

  if (type === 'investment') {
    if (/zerodha|groww|upstox|stock|equity/.test(d)) return 'Stocks';
    if (/sip|mutual fund|mf /.test(d)) return 'Mutual Fund';
    if (/etf/.test(d)) return 'ETF';
    if (/fd|fixed deposit/.test(d)) return 'Fixed Deposit';
    if (/crypto|bitcoin|btc|eth|coinbase/.test(d)) return 'Crypto';
    if (/gold|silver|metal/.test(d)) return 'Gold';
    if (/ppf|epf|nps/.test(d)) return 'PPF/EPF';
    if (/bond/.test(d)) return 'Bonds';
    if (/real estate|property/.test(d)) return 'Real Estate';
    return 'Other Investment';
  }

  // expense (default)
  for (const cat of Object.keys(KEYWORDS)) {
    for (const k of KEYWORDS[cat]) {
      if (d.includes(k)) return cat;
    }
  }
  return 'Miscellaneous';
}

// =============================================================================
// Phase A, Cash & Transfer detection
// =============================================================================
// `isAtmWithdrawalDescription` is the single source of truth that the import
// pipeline uses to decide "is this row a cash withdrawal that should NOT be
// counted as spending?". When true, we coerce type='transfer' so it stays out
// of expense aggregations and shows up only in the Cash account flow.
//
// Patterns cover Indian + global banks: HDFC "ATM-CASH-WDR", ICICI "NWD-",
// SBI "ATW", Axis "ATM/", Kotak "ATM-WDR", Chase "ATM WITHDRAWAL", etc.
const ATM_PATTERNS = [
  /\batm[\s\-:]?(?:cash|wdr|withdrawal|wdl)?\b/i,
  /\bcash[\s\-]?(?:wdr|wdl|withdrawal)\b/i,
  /\bnwd[\s\-]/i,                  // ICICI NWD- prefix
  /\batw[\s\-:]/i,                 // SBI ATW
  /\bcsh\s?wdl\b/i,
  /\bcash\s?at\s?atm\b/i,
];
export function isAtmWithdrawalDescription(description) {
  if (!description) return false;
  const d = String(description);
  return ATM_PATTERNS.some((re) => re.test(d));
}

// =============================================================================
// Phase 29, Refund detection
// =============================================================================
// `isRefundDescription` is the single source of truth for "is this incoming
// money actually a refund / reversal / cashback, not earned income?". When
// true, we coerce type='refund' so it stays out of the Income KPI and the
// Clarity Score's income_visibility component, while still being visible to
// the user as money received.
//
// Patterns cover Indian + global banks and major merchants:
//   • generic: REFUND, REVERSAL, REV, CHARGEBACK, RETURN, FAILED, RVRSL
//   • payments: FAILED UPI, FAILED TRANSACTION, REVERSED
//   • merchants: AMAZON PAY REFUND, SWIGGY REFUND, RAZORPAY REFUND,
//                FLIPKART REFUND, ZOMATO REFUND, MYNTRA REFUND
//   • rewards: CASHBACK, CASH BACK, REWARDS CREDIT
const REFUND_PATTERNS = [
  /\brefund(ed)?\b/i,
  /\brev(?:ersal|ersed|ersing)\b/i,
  /\brvrsl\b/i,
  /\bchargeback\b/i,
  /\bfailed\s+(?:upi|transaction|payment|transfer)\b/i,
  /\bcashback\b/i,
  /\bcash\s?back\b/i,
  /\brewards?\s+credit\b/i,
  /\bamazon\s?pay\s+refund\b/i,
  /\b(swiggy|zomato|flipkart|myntra|nykaa|amazon|razorpay)\s+refund\b/i,
  /\bpayu\s+refund\b/i,
  /\bipg\s+refund\b/i,
  /\brefund\s+(?:from|of)\b/i,
];
export function isRefundDescription(description) {
  if (!description) return false;
  const d = String(description);
  return REFUND_PATTERNS.some((re) => re.test(d));
}

// ===========================================================================
// Phase 70H — Taxonomy Lock. The category list and its size live in the one
// registry (shared/taxonomy/expense_categories.v1.json); this file only holds
// the merchant/narration rules that point AT those categories.
// ===========================================================================
//
// Design: stored as a flat string for back-compat. Grouping is purely a UI
// concern — the dropdown reads from EXPENSE_CATEGORY_GROUPS to render
// section headers; the DB only ever sees the leaf string.
//
// Why grouped: 24 items in an unsorted dropdown is harder to scan than 8
// section headers each with 1-8 items. Groups also act as memory aids
// ("where do I file insurance? → Financial group") and make the dropdown
// honest about WHY a category exists.
//
// Subcategories are internal-only — never shown in the form. They're
// stored on the transactions table so analytics, AI reflections and
// recurring detection can produce richer signals without making the UI
// more complicated. See merchant_dictionary table for population.
//
// Income / Investment / Transfer / Refund taxonomies are unchanged in
// 70H — they were already small and consistent.

// The list itself is NOT written here. It comes from the one canonical file,
// shared/taxonomy/expense_categories.v1.json, copied to
// src/lib/taxonomy.expense.json by scripts/sync_taxonomy.mjs; a backend test
// fails the build if the two ever differ. Engine, picker and tests read the
// same list (interim taxonomy 27.1).
export {
  TAXONOMY_VERSION,
  CATEGORY_DISPLAY_LABELS,
  categoryLabel,
  NON_SPENDING_CATEGORIES,
} from './taxonomyExpense.js';

import { EXPENSE_CATEGORY_GROUPS_FROM_TAXONOMY } from './taxonomyExpense.js';

export const EXPENSE_CATEGORY_GROUPS = EXPENSE_CATEGORY_GROUPS_FROM_TAXONOMY;

export const EXPENSE_CATEGORIES = EXPENSE_CATEGORY_GROUPS.flatMap((g) => g.items);

// Subcategory map (internal use only — not surfaced in the form).
// Used by:
//   - merchant_dictionary lookup (returns category + subcategory)
//   - future analytics / reflections
//   - Phase 70I migration when remapping historical rows
export const EXPENSE_SUBCATEGORIES = {
  'Food & Dining':   ['Restaurants','Cafes & Coffee','Quick Service','Food Delivery','Bars & Pubs','Bakery & Desserts'],
  'Groceries':       ['Supermarket','Kirana / Local','Online Grocery','Speciality'],
  'Fuel':            ['Petrol','Diesel','EV Charging'],
  'Transport':       ['Ride-hail','Auto-rickshaw','Metro / Train','Bus','Parking & Tolls','Two-wheeler'],
  'Utilities':       ['Electricity','Water','Gas','Internet','Mobile','Cable / DTH'],
  'Shopping':        ['Clothing & Apparel','Electronics','Home & Decor','Books & Stationery','Beauty & Cosmetics','Other'],
  'Medical':         ['Doctor / Clinic','Hospital','Pharmacy','Diagnostics','Dental','Optical'],
  'Personal Care':   ['Salon','Spa','Gym & Fitness','Wellness'],
  'Housing':         ['Rent','Furniture','Appliances','Security Deposit'],
  'Home Maintenance':['Repairs','Maintenance','Household Help'],
  'Education':       ['School Fees','Tuition','Courses','Books & Supplies'],
  'Childcare':       ['Daycare','Nanny','Baby Supplies','Kids\' Activities'],
  'Entertainment':   ['Movies','Events & Concerts','Gaming','Streaming Pay-per-view'],
  'Travel':          ['Flights','Hotels','Vacation Activities','Travel Insurance'],
  'Subscriptions':   ['Streaming','Software / SaaS','News & Media','Cloud Storage','Memberships'],
  'Hobbies':         ['Sports','Art & Crafts','Photography','Music','Other Hobby'],
  'Gifts':           ['Birthday','Wedding','Anniversary','Festive','Other Gift'],
  'Loan Payment':    ['Home Loan','Car Loan','Personal Loan','Education Loan','Credit Card Payment'],
  'Insurance':       ['Health','Vehicle','Life / Term','Property','Travel'],
  'Taxes':           ['Advance Tax','GST','Professional Tax','Property Tax','Income Tax'],
  'Fees & Charges':  ['Bank Charges','ATM Charges','Forex Markup','Late Fee','Convenience Fee'],
  'Business Expenses':['Client Meals','Office Supplies','Software & Tools','Marketing','Travel (Business)'],
  'Giving':          ['Charity / NGO','Religious Offering','Mutual Aid','Other Giving'],
  'Miscellaneous':   [],
  'Car Maintenance': ['Servicing','Repairs','Tyres','Insurance Claim','Accessories'],
  'Transfers':       [],
  'Investments':     [],
};

// Helper for the new grouped <Select> component. Returns either a flat
// array (income/investment/transfer/refund) or a grouped array (expense)
// so the UI layer can render headers conditionally without branching.
export function categoryGroupsForType(type) {
  if (type === 'expense' || type === undefined || type === null) {
    return EXPENSE_CATEGORY_GROUPS;
  }
  // Wrap non-expense flat lists in a single anonymous group so the UI
  // can use the same renderer in both cases.
  const flat = categoriesForType(type);
  return [{ key: type, label: '', items: flat }];
}

// ===========================================================================

export const INVESTMENT_CATEGORIES = [
  'Mutual Fund','Stocks','Fixed Deposit','ETF','Bonds','Crypto','Real Estate','Gold','PPF/EPF','Other Investment'
];

export const INCOME_CATEGORIES = [
  'Salary','Business','Freelance','Rental','Interest','Dividend','Bonus','Capital Gains','Refund','Gift',
  // Phase 71B — borrowed funds (payday loans, personal loans, etc.) landing
  // in a bank/wallet account. Type is 'income' because that's how it shows
  // up on the statement, but it's intentionally EXCLUDED from real income
  // KPIs in the Dashboard so it doesn't inflate your "money in" totals.
  'Loan Received',
  'Other Income',
];

export const TRANSFER_CATEGORIES = [
  'Cash Withdrawal','Cash Deposit','Bank Transfer','Internal Transfer','Cash Reconciliation',
  // Phase 71B — when paying down a credit card balance, the inflow shows
  // up as a positive amount on the credit-card statement (and a negative
  // on the funding bank account). Marking it as a Transfer / Credit Card
  // Payment keeps it out of both income and expense totals — it's just
  // money moving from one of your accounts to another (debt reduction).
  'Credit Card Payment',
  'Gift','Other Transfer',
];

// Categories that look like income on the statement but represent
// borrowed money rather than earnings. The Dashboard excludes these
// from the headline "Income" KPI and the income-trend chart.
// Phase 1 containment: the list itself now lives in lib/interimPolicy.js so
// every surface shares ONE definition. Re-exported here for existing importers.
export { BORROWING_INCOME_CATEGORIES } from './interimPolicy';

export const REFUND_CATEGORIES = [
  'Refunds & Reversals','Cashback','Failed Payment Reversal','Vendor Refund','Service Refund'
];

export function categoriesForType(type) {
  if (type === 'income') return INCOME_CATEGORIES;
  if (type === 'investment') return INVESTMENT_CATEGORIES;
  if (type === 'transfer') return TRANSFER_CATEGORIES;
  if (type === 'refund') return REFUND_CATEGORIES;
  return EXPENSE_CATEGORIES;
}

export const CATEGORY_COLORS = {
  // ---- Phase 70H new canonical categories ----
  // Daily Living
  'Food & Dining':     '#f97316',   // orange
  'Groceries':         '#22c55e',   // green
  'Fuel':              '#eab308',   // amber-yellow
  'Transport':         '#8b5cf6',   // violet
  'Car Maintenance':   '#7c3aed',   // deeper violet (sibling of Transport)
  'Utilities':         '#3b82f6',   // blue
  'Shopping':          '#ec4899',   // pink
  'Medical':           '#ef4444',   // red
  'Personal Care':     '#f472b6',   // soft pink
  // Home
  'Housing':           '#a855f7',   // purple (was 'Rent' colour)
  'Home Maintenance':  '#9333ea',   // deeper purple
  // Family
  'Education':         '#0ea5e9',   // sky blue
  'Childcare':         '#7dd3fc',   // light sky
  // Lifestyle
  'Entertainment':     '#d946ef',   // fuchsia
  'Travel':            '#0891b2',   // cyan-teal
  'Subscriptions':     '#06b6d4',   // cyan
  'Hobbies':           '#f59e0b',   // amber
  'Gifts':             '#fb7185',   // rose
  // Financial
  'Loan Payment':      '#dc2626',   // crimson (was 'Loans')
  'Insurance':         '#0d9488',   // teal
  'Taxes':             '#475569',   // slate
  'Fees & Charges':    '#64748b',   // muted slate
  'Transfers':         '#7c3aed',   // violet — P2P payments to people for services
  // Work
  'Business Expenses': '#334155',   // dark slate
  // Giving (warm, intentional)
  'Giving':            '#e11d48',   // warm rose-red — signals "heart"
  // Other
  'Miscellaneous':     '#94a3b8',

  // ---- Legacy category names kept for back-compat with existing rows ----
  // These are still shown for transactions that haven't been remapped by
  // the 70I historical migration yet. After 70I lands these should all
  // be gone from production data, but the colour map retains them so
  // donut charts of older periods don't fall back to grey.
  'Loans':                  '#dc2626',
  'Rent':                   '#a855f7',
  'Healthcare':             '#ef4444',
  'Health':                 '#ef4444',
  'Pharmacy':               '#ef4444',
  'Transportation':         '#8b5cf6',
  'Automotive':             '#8b5cf6',
  'Food Delivery':          '#f97316',
  'Dining':                 '#f97316',
  'Groceries & Food':       '#22c55e',
  'Bank Fees':              '#64748b',

  // income
  'Salary': '#10b981',
  'Business': '#0ea5e9',
  'Freelance': '#14b8a6',
  'Rental': '#a855f7',
  'Interest': '#84cc16',
  'Dividend': '#22c55e',
  'Bonus': '#10b981',
  'Refund': '#22c55e',
  // Phase 55 follow-up — Gift / family transfers (e.g. inheritance,
  // person-to-person inflows). Warm soft rose so it visually reads as
  // "received from someone you know" rather than transactional inflow.
  'Gift': '#e8a3a3',
  'Other Income': '#94a3b8',
  // investment
  'Mutual Fund': '#0ea5e9',
  'Stocks': '#10b981',
  'Fixed Deposit': '#14b8a6',
  'ETF': '#06b6d4',
  'Bonds': '#3b82f6',
  'Crypto': '#f59e0b',
  'Real Estate': '#a855f7',
  'Gold': '#eab308',
  'PPF/EPF': '#84cc16',
  'Other Investment': '#94a3b8',
  // transfer (neutral, intentionally muted, these don't count in spend/income totals)
  'Cash Withdrawal':     '#a8b3a3',
  'Cash Deposit':        '#a8b3a3',
  'Bank Transfer':       '#a8b3a3',
  'Internal Transfer':   '#a8b3a3',
  'Cash Reconciliation': '#a8b3a3',
  'Other Transfer':      '#a8b3a3',
  // LLM fallback / generic labels, hardcoded so they don't collide with
  // each other (or with curated categories) when the donut renders them.
  // 'Transfer' uses the neutral slate-green of the transfer family;
  // 'General' takes a distinct warm taupe so the two are visually obvious.
  'Transfer':            '#a8b3a3',
  'General':             '#c08457',
  'Other':               '#b08968',
  'Personal':            '#7e57c2',
  'Bank/Financial':      '#5c6bc0',
  'Uncategorized':       '#94a3b8',
  // refund (muted slate, visible but not credited as earned income)
  'Refunds & Reversals':     '#94a3b8',
  'Cashback':                '#94a3b8',
  'Failed Payment Reversal': '#94a3b8',
  'Vendor Refund':           '#94a3b8',
  'Service Refund':          '#94a3b8',
};

// ---------------------------------------------------------------------------
// Fallback colour generator for category names not in the curated map.
// LLM-generated categories (e.g. "Bank/Financial", "Food Delivery",
// "Apparel & Accessories", "Personal") fall here. We use the golden-ratio
// hue trick so successive (alphabetically nearby) names map to widely-
// separated hues — guaranteeing no two fallback categories ever produce
// the same colour, and avoiding the previous bug where the donut had four
// slices all the same slate-grey.
// ---------------------------------------------------------------------------
function _hashString(s) {
  // FNV-1a 32-bit — better bit-mixing than naive `h*31+ch`. Important when
  // mapping a tiny set of similar strings ("Personal" vs "Apparel &
  // Accessories" etc.) onto a 360-degree hue circle: a weak hash like the
  // Java-default kept producing hues within 5\u00b0 of each other, which is
  // visually indistinguishable. FNV-1a scatters them properly.
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

const _PHI = 0.618033988749895; // golden ratio fractional part

/** Return a stable, visually distinct colour for any category name.
 *  Uses the curated `CATEGORY_COLORS` map first (for our canonical
 *  categories), then falls back to an HSL hue derived from a hash of the
 *  name via the golden-ratio trick — guaranteeing that any two distinct
 *  category names get different colours. */
export function colorForCategory(name) {
  if (!name) return '#94a3b8';
  const direct = CATEGORY_COLORS[name];
  if (direct) return direct;
  const h = _hashString(String(name).toLowerCase());
  // Multiply by golden ratio (in [0,1)) so consecutive integers spread
  // evenly across the [0,360) hue circle.
  const hue = Math.floor(((h * _PHI) % 1) * 360);
  // Saturation 62% / Lightness 52% looks well-balanced against both
  // light and dark backgrounds and reads similarly to the curated tones.
  return `hsl(${hue} 62% 52%)`;
}

// Asset / Liability categories for Net Worth
export const ASSET_CATEGORIES = [
  'Savings','Current Account','Fixed Deposit','Mutual Fund','Stocks','ETF','Bonds',
  'PPF/EPF','NPS','Real Estate','Gold','Crypto','Vehicle','Other Asset'
];
export const LIABILITY_CATEGORIES = [
  'Home Loan','Personal Loan','Car Loan','Education Loan','Credit Card','EMI','Other Liability'
];

export const ACCOUNT_TYPES = [
  // Phase 60B: Accounts is now narrowly scoped to bank / cash / wallet /
  // credit card. Investments live in Wealth (mutual funds, stocks,
  // deposits, bonds, gold), loans live in Liabilities. The values below
  // are kept in the codebase for back-compat with legacy rows but are
  // no longer surfaced to the user when creating a new account.
  { value: 'bank', label: 'Bank account' },
  // Phase 86.K — the Cash & Savings sub-tabs show the account type, so the type
  // has to be specific enough to be worth showing.
  { value: 'savings', label: 'Savings account' },
  { value: 'chequing', label: 'Current / chequing account' },
  { value: 'money_market', label: 'Money market account' },
  { value: 'credit_card', label: 'Credit card' },
  { value: 'wallet', label: 'Digital wallet' },
  { value: 'cash', label: 'Physical cash' },
  { value: 'prepaid', label: 'Prepaid balance' },
  { value: 'mobile_money', label: 'Mobile money' },
  { value: 'other', label: 'Other' },
  // ---- Deprecated, retained for back-compat reads only ----
  // { value: 'demat',      label: 'Demat / Broker' },
  // { value: 'investment', label: 'Investment account' },
  // { value: 'deposit',    label: 'Deposit (FD / RD)' },  // → Wealth → Deposits
];
